/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { Template } from '@microsoft/esign.gateway.service.client';
import { UploadStatus } from '@msteams/services-io-browser-file-upload';
import { useBoolean } from '@uifabric/react-hooks';
import * as React from 'react';

import { docusignAccountSettingsVar } from '../../../apollo/reactiveVariables/EsignProviderVariables';
import { Constants } from '../../../common/Constants';
import { ApprovalRequestType } from '../../../data/types.generated';
import { LogCategory, PanelRegion, PanelType, ScenarioName, StepName, SubWorkLoad } from '../../../logger';
import { MobileFilePickerComponent } from '../../../mobile/components/MobileFileAttachments/MobileFileAttachmentsComponent';
import { AdobeFileAttachment } from '../../../models/fileService/AdobeFileAttachment';
import { DocusignFileAttachment } from '../../../models/fileService/DocusignFileAttachment';
import { FileAttachment } from '../../../models/fileService/FileAttachment';
import { FileService } from '../../../models/fileService/FileService';
import { IFilePickerItem } from '../../../models/sharePointPicker';
import { IconName, getThemedIcon } from '../../../resources/icons/Icons';
import { AuthenticationService } from '../../../service/file-upload/auth/auth-service';
import { getFluidSharePointInfo } from '../../../service/file-upload/GetFluidSharePointInfo';
import { IFluidSharepointInfo } from '../../../service/file-upload/services-io-fluid/fluid-model.interface';
import { FluidService } from '../../../service/file-upload/services-io-fluid/fluid-service';
import { ApprovalType } from '../../../service/gen';
import { SignApprovalType } from '../../../service/gen/esign';
import { getNewFileName, getReadableFileSize } from '../../../utilities/FileUtils';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { useInitializeDocusignAccountSettings } from '../../../utilities/UserSettings/DocusignProviderAccountSettings';
import { stringFormat } from '../../../utilities/Utils';
import { ConfirmationDialogProps, SharePointPickerEntryPoint } from '../../common';
import { FileChiclet } from '../../components';
import { FileAttachmentsComponent } from '../../components/FileAttachments/FileAttachments.component';
import { ConflictResolutionModalReturnType } from '../utils/types';

export interface EsignFileAttachmentProps {
    fileService?: FileService<FileAttachment>;
    showLabel?: boolean;
    accept?: string;
    viewOnly?: boolean;
    files: FileAttachment[];
    onFilesUpdate?: (files: FileAttachment[]) => void;
    signApprovalType?: SignApprovalType;
    required?: boolean;
    isOfficeAddin?: boolean;
    onFileClicked?: (file: FileAttachment) => Promise<void>;
}

export const EsignFileAttachment: React.FC<EsignFileAttachmentProps> = (
    props: EsignFileAttachmentProps
): JSX.Element => {
    const {
        fileService,
        showLabel,
        accept,
        viewOnly,
        files,
        onFilesUpdate,
        signApprovalType,
        required,
        isOfficeAddin,
        onFileClicked,
    } = props;

    const [confirmationDialogProps, setConfirmationDialogProps] = React.useState<ConfirmationDialogProps>(null);
    const [validationError, setValidationError] = React.useState('');
    const [esignTemplateMenuItems, setEsignTemplateMenuItems] = React.useState<IContextualMenuItem[]>([]);
    const [siteInfo, setSiteInfo] = React.useState<IFluidSharepointInfo>(null);
    const uploadIndex = React.useRef(0);
    const fileUploadRef = React.useRef<HTMLInputElement>();
    const filesRef = React.useRef<FileAttachment[]>([]);

    const host = getAppHost();
    const ecs = host.getEcsConfig().MicrosoftTeamApprovals;
    const docusignAccountSettings = useReactiveVar(docusignAccountSettingsVar);
    const [isEsignTemplateOpen, { setTrue: openEsignTemplatePicker, setFalse: dismissEsignTemplatePicker }] =
        useBoolean(false);

    const uploadButtonDisabled = isOfficeAddin || (files && files.length >= Constants.FILE_ATTACHMENTS_ESIGN_MAX_COUNT);

    const loc = getAppHost().getLocale();
    const logger = getAppHost().getLogger();
    useInitializeDocusignAccountSettings(signApprovalType === SignApprovalType.Docusign);

    const selectCommandLabel = loc.strings.sharepoint_picker_download_file_label;
    const approvalType = ApprovalType.ESign.toString();
    filesRef.current = files;

    React.useEffect((): void => {
        if (isOfficeAddin) {
            return;
        }
        getFluidSharePointInfo(logger)
            .then((result: IFluidSharepointInfo): void => setSiteInfo(result))
            .catch();
    }, [isOfficeAddin, logger]);

    const shouldRenderFilePicker = (approvalType: string, uploadFromOneDrive: boolean): boolean => {
        return !(!uploadFromOneDrive && approvalType === ApprovalType.ESign.toString());
    };

    const onLocalUploadClick = (): void => {
        fileUploadRef.current.click();
    };

    const addFile = (fileAttachment: FileAttachment): void => {
        const newFiles = [...filesRef.current, { ...fileAttachment, progress: 0 }];
        onFilesUpdateAndValidate(newFiles);
    };

    const updateFile = (fileAttachment: FileAttachment): void => {
        const newFiles = filesRef.current.map(
            (f: FileAttachment): FileAttachment => (f.id === fileAttachment.id ? { ...fileAttachment } : f)
        );
        onFilesUpdateAndValidate(newFiles);
    };

    const removeFile = (fileAttachment: FileAttachment): void => {
        const newFiles = filesRef.current.filter((f: FileAttachment): boolean => f.id !== fileAttachment.id);
        onFilesUpdateAndValidate(newFiles);
    };

    const requiredFieldValidation = (fileAttachments: FileAttachment[]): void => {
        if (required) {
            if (fileAttachments && fileAttachments.length > 0) {
                setValidationError('');
            } else {
                setValidationError(loc.strings.required);
            }
        }
    };

    const onAttachmentBlur = (): void => {
        requiredFieldValidation(files);
    };

    const onFilesUpdateAndValidate = (fileAttachments: FileAttachment[]): void => {
        if (onFilesUpdate) {
            onFilesUpdate(fileAttachments);
            requiredFieldValidation(fileAttachments);
        }
    };

    const dismiss = (): void => {
        setConfirmationDialogProps(null);
    };

    const errorDialogProps: ConfirmationDialogProps = {
        isOpen: true,
        title: loc.strings.dialog_title_upload_failed,
        dismiss,
        actions: [
            {
                text: loc.strings.action_ok,
                onClick: dismiss,
            },
        ],
    };

    const getErrorInfo = (fileName: string, error: any): string => {
        let message: string = null;

        // TODO: localize all uploading error messages
        if (typeof error === 'string' || error instanceof String) {
            message = error.toString();
        } else {
            message = error.message || error.code;
        }

        if (message) {
            return message;
        }

        return stringFormat(loc.strings.upload_error_default, { file: fileName });
    };

    const getConflictResolutionModal = (
        file: File,
        fileAttachment: FileAttachment,
        originalName: string,
        renameIndex: number,
        uploadFunction: (
            file: File,
            fileAttachment: FileAttachment,
            overwrite: boolean,
            originalName: string,
            renameIndex?: number
        ) => Promise<void>
    ): ConflictResolutionModalReturnType => {
        const cancel = (): void => {
            removeFile(fileAttachment);
            dismiss();
        };
        const keepBoth = (): void => {
            // Rename file name for uploading
            const newName = getNewFileName(originalName, renameIndex);
            const newFile = new File([file], newName);
            fileAttachment.name = newName;
            updateFile(fileAttachment);

            uploadFunction(newFile, fileAttachment, false, originalName, renameIndex + 1);
            dismiss();
        };
        const replace = (): void => {
            uploadFunction(file, fileAttachment, true, originalName);
            dismiss();
        };
        const content = (
            <>
                <FileChiclet
                    viewOnly
                    file={{
                        ...fileAttachment,
                        progress: null,
                    }}
                    onChicletClicked={onFileClicked}
                />
                <div style={{ marginTop: '0.5em' }}>{loc.strings.dialog_subtext_conflicted_files}</div>
            </>
        );

        return {
            content,
            cancel,
            keepBoth,
            replace,
        };
    };

    const resolveFileUploadError = (
        file: File,
        fileAttachment: FileAttachment,
        overwrite: boolean,
        originalName: string,
        renameIndex: number,
        uploadError: any,
        uploadFunction: (
            file: File,
            fileAttachment: FileAttachment,
            overwrite: boolean,
            originalName: string,
            renameIndex?: number
        ) => Promise<void>
    ): void => {
        if (!overwrite && fileAttachment.status === UploadStatus.CONFLICT) {
            const conflictResolutionProps = getConflictResolutionModal(
                file,
                fileAttachment,
                originalName,
                renameIndex,
                uploadFunction
            );
            setConfirmationDialogProps({
                isOpen: true,
                title: loc.strings.dialog_title_conflicted_files,
                content: conflictResolutionProps.content,
                dismiss: conflictResolutionProps.cancel,
                actions: [
                    {
                        text: loc.strings.action_cancel,
                        onClick: conflictResolutionProps.cancel,
                    },
                    {
                        text: loc.strings.action_keep_both,
                        onClick: conflictResolutionProps.keepBoth,
                    },
                    {
                        text: loc.strings.action_replace,
                        onClick: conflictResolutionProps.replace,
                        primary: true,
                    },
                ],
            });
        } else {
            removeFile(fileAttachment);
            setConfirmationDialogProps({
                ...errorDialogProps,
                subText: getErrorInfo(fileAttachment.name, uploadError),
            });
        }
    };

    const onFileSelected = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const selectedFile = event.target.files[0];

        if (!selectedFile) {
            setConfirmationDialogProps({
                ...errorDialogProps,
                subText: loc.strings.upload_error_no_file_selected,
            });
            return;
        }

        if (selectedFile.size === 0) {
            setConfirmationDialogProps({
                ...errorDialogProps,
                subText: loc.strings.upload_error_empty,
            });
            return;
        }

        // Reset event target to allow subsequent uploading of the same file
        event.target.value = null;

        const duplicatedFiles = files.filter((file: FileAttachment): boolean => {
            return (
                file &&
                file.name === selectedFile.name &&
                file.type === selectedFile.type &&
                file.size === selectedFile.size &&
                file.lastModified === selectedFile.lastModified
            );
        });

        // Return without action if file is already chosen
        if (duplicatedFiles && duplicatedFiles.length > 0) {
            setConfirmationDialogProps({
                ...errorDialogProps,
                subText: loc.strings.upload_error_file_already_selected,
            });
            return;
        }

        const fileAttachment: FileAttachment = {
            name: selectedFile.name,
            type: selectedFile.type,
            size: selectedFile.size,
            lastModified: selectedFile.lastModified,
            description: getReadableFileSize(selectedFile.size, loc),
            id: uploadIndex.current++,
        };

        addFile(fileAttachment);

        try {
            uploadFile(selectedFile, fileAttachment, false, selectedFile.name);
        } catch (uploadError) {
            removeFile(fileAttachment);
            setConfirmationDialogProps({
                ...errorDialogProps,
                subText: getErrorInfo(fileAttachment.name, uploadError),
            });
        }
    };

    const uploadFile = async (
        selectedFile: File,
        fileAttachment: FileAttachment,
        overwrite: boolean,
        originalName: string,
        renameIndex: number = 1
    ): Promise<void> => {
        const notifier = (progress: number, status: UploadStatus): void => {
            fileAttachment.progress = progress;
            fileAttachment.status = status;
            updateFile(fileAttachment);
        };

        try {
            const result = await props.fileService.uploadFile(selectedFile, notifier, overwrite);
            Object.assign(fileAttachment, result);
            updateFile(fileAttachment);
        } catch (uploadError) {
            resolveFileUploadError(
                selectedFile,
                fileAttachment,
                overwrite,
                originalName,
                renameIndex,
                uploadError,
                uploadFile
            );
        }
    };

    const onFileUploadClicked = (): void => {
        const approvalType: ApprovalRequestType =
            signApprovalType === SignApprovalType.Adobe
                ? ApprovalRequestType.ESign
                : signApprovalType === SignApprovalType.Docusign
                ? ApprovalRequestType.Docusign
                : ApprovalRequestType.Basic;
        logger.logEsignUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.CreateEsignApproval,
            StepName.FileUploadClicked,
            'click',
            'nav',
            'fileAttachment',
            'input',
            PanelRegion.Modal,
            PanelType.CreateEsignApproval,
            {
                ApprovalType: approvalType,
            }
        );
    };

    const onTemplateSelect = (template: Template): void => {
        let fileAttachment: AdobeFileAttachment | DocusignFileAttachment = {
            name: template.name,
            description: null,
            id: uploadIndex.current++,
            status: UploadStatus.COMPLETE,
        };
        if (signApprovalType === SignApprovalType.Adobe) {
            fileAttachment = { ...fileAttachment, name: `${fileAttachment.name}.pdf`, libraryDocumentId: template.id };
        }

        if (signApprovalType === SignApprovalType.Docusign) {
            fileAttachment = { ...fileAttachment, templateId: template.id };
        }

        addFile(fileAttachment);
    };

    const uploadFileToFileService = async (
        selectedFile: File,
        fileAttachment: FileAttachment,
        overwrite: boolean,
        originalName: string,
        renameIndex: number = 1
    ): Promise<void> => {
        const notifier = (progress: number, status: UploadStatus): void => {
            fileAttachment.progress = progress;
            fileAttachment.status = status;
            updateFile(fileAttachment);
        };

        try {
            const result = await fileService.uploadFile(selectedFile, notifier, overwrite);
            Object.assign(fileAttachment, result);
            updateFile(fileAttachment);
        } catch (uploadError) {
            resolveFileUploadError(
                selectedFile,
                fileAttachment,
                overwrite,
                originalName,
                renameIndex,
                uploadError,
                uploadFileToFileService
            );
        }
    };

    const onSharePointPickerSelect = (
        items: IFilePickerItem[],
        entryPoint: SharePointPickerEntryPoint
    ): Promise<string> => {
        const approvalType: ApprovalRequestType =
            signApprovalType === SignApprovalType.Adobe
                ? ApprovalRequestType.ESign
                : signApprovalType === SignApprovalType.Docusign
                ? ApprovalRequestType.Docusign
                : ApprovalRequestType.Basic;
        logger.logEsignUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.CreateEsignApproval,
            entryPoint,
            'click',
            'nav',
            'sharepoint-picker-iframe',
            'iframe',
            PanelRegion.Modal,
            PanelType.CreateEsignApproval,
            {
                ApprovalType: approvalType,
            }
        );
        if (items && items.length > 0) {
            const file = items[0];
            const fileAttachment: FileAttachment = {
                id: uploadIndex.current++,
                name: file.name ?? '',
                description: file.description,
                size: file.size,
            };

            addFile(fileAttachment);
            logger.logTraceInfo(LogCategory.SharePointPicker, `File chiclet added, entryPoint: ${entryPoint}`);

            const driveId = file.parentReference?.driveId
                ? file.parentReference?.driveId
                : file.remoteItem?.parentReference?.driveId;
            const itemId = file.id;
            if (driveId && itemId) {
                return new FluidService(new AuthenticationService())
                    .getFileContentById(itemId, driveId)
                    .then((fileBlob: Blob): string => {
                        fileAttachment.status = UploadStatus.PROGRESS;
                        const file = new File([fileBlob], fileAttachment.name, { type: fileBlob.type });
                        uploadFileToFileService(file, fileAttachment, false, file.name);
                        return 'File blob retrieved';
                    })
                    .catch((reason: string): string => {
                        removeFile(fileAttachment);
                        setConfirmationDialogProps({
                            ...errorDialogProps,
                            subText: stringFormat(loc.strings.share_file_error, { file: file.name }),
                        });
                        throw reason;
                    });
            } else {
                // Remove file chiclet if driveId or itemId missing
                removeFile(fileAttachment);
                setConfirmationDialogProps({
                    ...errorDialogProps,
                    subText: stringFormat(loc.strings.share_file_error, { file: file.name }),
                });
                return Promise.reject('DriveId or itemId missing for getting download URL');
            }
        } else {
            setConfirmationDialogProps({
                ...errorDialogProps,
                subText: loc.strings.sharepoint_picker_error_no_item,
            });
            return Promise.reject('No item selected');
        }
    };

    React.useEffect((): void => {
        const adobeEnabled = signApprovalType === SignApprovalType.Adobe;
        const docusignEnabled =
            signApprovalType === SignApprovalType.Docusign && docusignAccountSettings?.allowTemplates;
        if (approvalType === ApprovalType.ESign.toString() && (adobeEnabled || docusignEnabled)) {
            const text = adobeEnabled
                ? loc.strings.adobe_sign_library_button_label
                : loc.strings.docusign_template_button_label;
            const icon = adobeEnabled ? IconName.adobeCreateIcon : IconName.docusignCreateIcon;
            setEsignTemplateMenuItems([
                {
                    key: 'templates',
                    text,
                    iconProps: {},
                    onRenderIcon: (): JSX.Element => <i className='upload-icon'>{getThemedIcon(icon)}</i>,
                    onClick: openEsignTemplatePicker,
                },
            ]);
        }
    }, [ecs, getThemedIcon, approvalType, docusignAccountSettings]);

    const desktopComponent = (
        <FileAttachmentsComponent
            showLabel={showLabel}
            viewOnly={viewOnly || isOfficeAddin}
            selectCommandLabel={selectCommandLabel}
            uploadButtonDisabled={uploadButtonDisabled}
            confirmationDialogProps={confirmationDialogProps}
            esignTemplateMenuItems={esignTemplateMenuItems}
            isEsignTemplateOpen={isEsignTemplateOpen}
            renderFilePicker={shouldRenderFilePicker(approvalType, ecs.ESignUploadFileFromOneDriveEnabled)}
            dismissEsignTemplatePicker={dismissEsignTemplatePicker}
            fileUploadRef={fileUploadRef}
            accept={accept}
            files={files}
            onFileSelected={onFileSelected}
            onFileUploadClicked={onFileUploadClicked}
            removeFile={removeFile}
            onLocalUploadClick={onLocalUploadClick}
            approvalType={approvalType}
            signApprovalType={signApprovalType}
            onSharePointPickerSelect={onSharePointPickerSelect}
            onTemplateSelect={onTemplateSelect}
            focusListener={onAttachmentBlur}
            validationError={validationError}
            required={required}
            siteInfo={siteInfo}
            isOfficeAddin={isOfficeAddin}
            isEsign={true}
            onFileClicked={onFileClicked}
        />
    );

    const mobileComponent = (
        <MobileFilePickerComponent
            showLabel={showLabel}
            viewOnly={viewOnly}
            selectCommandLabel={selectCommandLabel}
            uploadButtonDisabled={uploadButtonDisabled}
            confirmationDialogProps={confirmationDialogProps}
            fileUploadRef={fileUploadRef}
            accept={accept}
            files={files}
            onFileSelected={onFileSelected}
            onFileUploadClicked={onFileUploadClicked}
            removeFile={removeFile}
            onLocalUploadClick={onLocalUploadClick}
            required={required}
            onFileClicked={onFileClicked}
        />
    );

    return getAppHost().isMobile() ? mobileComponent : desktopComponent;
};
